'use client'

import { Text } from '@macarongfactory/web-ui'
import { useState } from 'react'

import PlatformGraphic from './PlatformGraphic'
import { isAlreadyBrokenImage } from '../_features/modoocar'

interface ICarImageProps {
  company: string
  modelName: string
  /** 제조사 로고 이미지 URL (자동차 icon_car / 모터사이클 icon_bike — 호출부에서 분기) */
  brandLogoUrl: string
  /**
   * 차종 대표 이미지 URL 후보(우선순위 순). 앞에서부터 시도하고 로드 실패 시
   * 다음 후보로 넘어간다. 모두 실패하면 fallbackImageUrl → 텍스트 순으로 폴백.
   * (리소스 서버 이미지 키가 차종마다 세대명/차종명으로 제각각이라 폴백 체인 필요)
   */
  carImageUrls: string[]
  /** 모든 차종 이미지 실패 시 로띠(웨이브) 없이 노출할 기본 일러스트 URL */
  fallbackImageUrl?: string
}

/**
 * 디자이너 제공 히어로 배경(브랜드 블루 0% → 흰색 그라데이션).
 * DS 토큰으로 표현 불가해 디자인 값 그대로 적용한다.
 */
const HERO_BACKGROUND =
  'linear-gradient(180deg, rgba(0, 175, 255, 0.00) 0%, rgba(0, 175, 255, 0.30) 50%, rgba(0, 175, 255, 0.20) 85.1%, rgba(0, 175, 255, 0.00) 100%), #FFF'

/**
 * 히어로 차량 이미지 영역 (Figma 기준)
 * - 차종 사진: 중앙 메인 이미지
 * - 제조사 로고: 우상단 작은 배지(연파랑 informative-weak 배경)
 */
const CarImage = ({
  company,
  modelName,
  brandLogoUrl,
  carImageUrls,
  fallbackImageUrl,
}: ICarImageProps) => {
  // 현재 시도 중인 이미지 후보 인덱스. 후보를 모두 소진하면 폴백 일러스트 → 텍스트.
  const [srcIndex, setSrcIndex] = useState(0)
  // 실패한 후보에서만 한 칸 넘긴다 — ref(StrictMode 이중 호출)·onError가 겹쳐 불려도 후보를 건너뛰지 않게
  const skipFailedImage = () =>
    setSrcIndex(prev => (prev === srcIndex ? prev + 1 : prev))
  const [fallbackError, setFallbackError] = useState(false)
  const [brandError, setBrandError] = useState(false)

  const currentCarImageUrl = carImageUrls[srcIndex]
  const carError = !currentCarImageUrl
  const showFallback = carError && fallbackImageUrl && !fallbackError

  return (
    <div
      className='relative flex w-full flex-col items-center self-stretch px-dimension-x5 pb-dimension-x2 pt-dimension-x16 lg:rounded-t-dimension-x5'
      style={{ background: HERO_BACKGROUND }}
    >
      {/* 이미지 영역 — Figma 카드(360×238, pad T64·R20·B8·L20)의 콘텐츠 320×166.
          차 사진은 상단 중앙, wave 그래픽은 하단 밴드로 겹친다. */}
      <div className='relative mx-auto aspect-[320/166] w-full max-w-[320px]'>
        {/* wave 그래픽 — 차종 이미지가 있을 때만 노출. 기본 일러스트/텍스트 폴백에서는 숨김 */}
        {!carError && (
          <PlatformGraphic className='pointer-events-none absolute inset-x-[6.25%] top-[6%] h-[84.3%] lg:top-[15.7%]' />
        )}
        {carError ? (
          showFallback ? (
            <img
              ref={node => {
                if (isAlreadyBrokenImage(node)) setFallbackError(true)
              }}
              src={fallbackImageUrl}
              alt={`${company} ${modelName}`}
              className='absolute left-1/2 top-0 h-[114px] w-dimension-x50 -translate-x-1/2 object-contain lg:h-[136px] lg:w-dimension-x60'
              onError={() => setFallbackError(true)}
            />
          ) : (
            <div className='absolute inset-x-0 top-0 flex h-[114px] items-center justify-center lg:h-[136px]'>
              <Text
                variant='body-2-medium'
                color='text-foreground-neutral-assistive'
              >
                {company} {modelName}
              </Text>
            </div>
          )
        ) : (
          <img
            key={currentCarImageUrl}
            ref={node => {
              if (isAlreadyBrokenImage(node)) skipFailedImage()
            }}
            src={currentCarImageUrl}
            alt={`${company} ${modelName}`}
            className='absolute left-1/2 top-0 z-[1] h-[114px] w-dimension-x50 -translate-x-1/2 object-contain lg:h-[136px] lg:w-dimension-x60'
            onError={skipFailedImage}
          />
        )}
      </div>

      {/* 제조사 로고 (우상단) — width 64px(dimension-x16), top 10px(dimension-x2-5) / right 30px(토큰 없음) */}
      {brandError ? null : (
        <div className='absolute right-[30px] top-dimension-x2-5 flex w-dimension-x16 items-center'>
          <img
            src={brandLogoUrl}
            alt={`${company} 로고`}
            className='w-full object-contain'
            onError={() => setBrandError(true)}
          />
        </div>
      )}
    </div>
  )
}

export default CarImage
